import { useMemo, useState } from 'react';
import { toIso2Code } from '../../lib/helpers/IsoCodeHelper';
import DebouncedInput from '../DebouncedInput';
import { ProductImage } from '../product/ProductImage';
import CustomerAddress from '../smartsend/CustomerAddress';
import ItemTag from '../smartsend/item/ItemTag';
import { IsBillingAddress, IsDefaultAddress } from '../../lib/helpers/IsDefaultAddress';
import { Customer } from '~/lib/api/smartsend/types';
import { P2GBox, P2GGrid, P2GText, P2GLink } from '@parcel2go/core-ui';
import { AppCard } from '~/components/card/app-card';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

interface props {
  customers: Customer[];
  onSelect: (p: Customer) => void;
}

const isMatch = (x: Customer, s: string) => {
  return !s || JSON.stringify(x).toLowerCase().indexOf(s.toLowerCase()) > -1;
};

export const CustomerList: React.FC<props> = (props) => {
  const [search, setSearch] = useState('');
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const customers = useMemo(() => {
    return props.customers.filter((x) => isMatch(x, search));
  }, [props.customers, search]);

  return (
    <>
      <DebouncedInput
        value={search}
        onChange={setSearch}
        id="Search"
        label={t('SEARCH_INPUT_LABEL', { defaultValue: 'Search...' })}
      />
      <P2GBox mt={2}>
        <P2GGrid container direction="column" spacing={2}>
          {customers.map((x) => (
            <P2GGrid item key={`${x.Id}_${x.CountryIsoCode}_${x.Postcode}`}>
              <AppCard>
                <P2GBox p={2} onClick={() => props.onSelect(x)}>
                  <P2GGrid container wrap="nowrap" alignItems="center" gap={2}>
                    <P2GGrid item flexShrink={1} key={`${x.Id}_${x.CountryIsoCode}`}>
                      <ProductImage
                        style={{ height: '20px' }}
                        product={{
                          ImageUrl: `/~/.flags/svg/${toIso2Code(x.CountryIsoCode).toLocaleLowerCase()}.svg`
                        }}
                      />
                    </P2GGrid>
                    <P2GGrid item flexGrow={1} data-sentry-mask>
                      <P2GBox>
                        <P2GText>
                          <strong>{x.Name}</strong>
                        </P2GText>
                      </P2GBox>
                      <P2GBox>
                        <CustomerAddress customer={x} />
                      </P2GBox>
                      <P2GBox>
                        <P2GText variant="caption">
                          {x.CountryIsoCode} {x.CountrySubdivision}
                        </P2GText>
                      </P2GBox>
                      <P2GBox>
                        <P2GGrid container spacing={1}>
                          {IsDefaultAddress(x) && (
                            <P2GGrid item>
                              <ItemTag tag={'Default'} onRemove={undefined} color="primary" />
                            </P2GGrid>
                          )}
                          {IsBillingAddress(x) && (
                            <P2GGrid item>
                              <ItemTag tag={'Billing'} onRemove={undefined} color="primary" />
                            </P2GGrid>
                          )}
                        </P2GGrid>
                      </P2GBox>
                    </P2GGrid>
                    <P2GGrid item flexShrink={1}>
                      <P2GLink>{t('SELECT_BUTTON', { defaultValue: 'Select' })}</P2GLink>
                    </P2GGrid>
                  </P2GGrid>
                </P2GBox>
              </AppCard>
            </P2GGrid>
          ))}
        </P2GGrid>
      </P2GBox>
    </>
  );
};
